import * as React from "react"
import { cn } from "@uconnect/assets/lib/utils"

/**
 * WebKit (Safari, legacy Edge) makes an inline <svg> — and every shape element inside it (path,
 * line, polygon, circle, etc.) — individually keyboard-focusable, one tab stop apiece.
 * `aria-hidden`/`focusable="false"` on the markup don't stop it in practice; the real DOM
 * `tabIndex` property is what every browser actually honors for Tab order. Baking `tabIndex={-1}`
 * into every node here (once, at render time) covers it without a runtime script having to walk
 * and patch the DOM after the fact.
 */
function markNotTabbable(node: React.ReactNode): React.ReactNode {
	if (!React.isValidElement(node)) return node

	const props = node.props as { children?: React.ReactNode }
	const children = props.children
		? React.Children.map(props.children, markNotTabbable)
		: props.children

	if (node.type === React.Fragment) {
		return React.cloneElement(node, {}, children)
	}

	return React.cloneElement(node as React.ReactElement<any>, { tabIndex: -1 }, children)
}

export default function Icon(
	{ className, title, name, children, ...props }: React.ComponentProps<"svg"> &
	// Non-SVG types
	{
		title?: string;
		name?: string;
	},
) {
	return (
		<svg
			className={cn("icon--editable-stroke inline-block", className)}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			width="1em"
			height="1em"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.5}
			data-cap="butt" // React does not recognize `dataCap`; use `data-cap` instead
			strokeLinecap="round"
			strokeLinejoin="round"
			role="img"
			tabIndex={-1}
			{...(title ? {} : { "aria-hidden": true })}
			{...props}
		>
			{title ? <title>{title}</title> : null}
			{typeof name === "string" ? markNotTabbable(ICONS[name]) ?? null : name}
		</svg>
	)
}

const ICONS: Record<string, JSX.Element> = {
	arrowDiagonalOut: (
		<>
			<path d="M9 5L5 5C3.89543 5 3 5.89543 3 7L3 19C3 20.1046 3.89543 21 5 21L17 21C18.1046 21 19 20.1046 19 19L19 15" />
			<path d="M12 12L21 3L20.6972 3.30285" />
			<path d="M14 3L21 3L21 10" />
		</>
	),
	arrowDown: (
		<>
			<path d="M12 3V21V20.5" />
			<path d="M19 14L12 21L5 14" />
		</>
	),
	arrowLeft: (
		<>
			<path d="M21 12L3 12L3.5 12" />
			<path d="M10 19L3 12L10 5" />
		</>
	),
	arrowRight: (
		<>
			<path d="M3 12L21 12L20.5 12" />
			<path d="M14 19L21 12L14 5" />
		</>
	),
	arrowTrendDown: (
		<>
			<path d="M1.5 8.5L7 14L13 8L22 17L21.6312 16.6312" />
			<path d="M15 17H22V10" />
		</>
	),
	arrowsExpand: (
		<>
			<path d="M15.5 8.5L21 3L20.7282 3.2718" />
			<path d="M15.5 15.5L21 21L20.7884 20.7884" />
			<path d="M8.5 8.5L3 3L3.19067 3.19067" />
			<path d="M8.5 15.5L3 21L3.13528 20.8647" />
			<path d="M16.5001 3.00003L21 3L21.0001 7.50003" />
			<path d="M16.5001 21L21 21L21.0001 16.5" />
			<path d="M3.00003 7.49999L3 3.00006L7.50003 2.99999" />
			<path d="M3.00003 16.5001L3 21L7.50003 21.0001" />
		</>
	),
	arrowTrendUp: (
		<>
			<path d="M1.5 15.5L7 10L13 16L22 7L21.6312 7.36884" />
			<path d="M15 7H22V14" />
		</>
	),
	arrowUp: (
		<>
			<path d="M12 3V21V3.5" />
			<path d="M19 10L12 3L5 10" />
		</>
	),
	bolt: (
		<>
			<path d="M14.5128 2H8.15898L5.46667 14H10.9231L10.4667 20.7059L19.5 9.5H12.9667L14.5128 2Z" />
		</>
	),
	boltSpeed: (
		<>
			<path d="M15.1 3L5.5 13.9548H12.4333L11.9 21L21.5 10.0452H14.5667L15.1 3Z" />
			<path d="M3 5H8" />
			<path d="M4 19H8" />
			<path d="M1 9H3" />
		</>
	),
	briefcase: (
		<>
			<path d="M7 6V20" />
			<path d="M17 6V20" />
			<path d="M9 6V3C9 2.44772 9.44772 2 10 2H14C14.5523 2 15 2.44772 15 3V6" />
			<path d="M3 8C3 6.89543 3.89543 6 5 6H19C20.1046 6 21 6.89543 21 8V18C21 19.1046 20.1046 20 19 20H5C3.89543 20 3 19.1046 3 18V8Z" />
		</>
	),
	briefcaseVideo: (
		<>
			<rect x="1" y="5" width="22" height="18" />
			<polyline points="8,5 8,1 16,1 16,5" />
			<polygon points=" 10,18 10,10 16,14" />
		</>
	),
	buildings: (
		<>
			<polyline points="5 23 1 23 1 13 5 13" />
			<line x1="9" y1="16" x2="11" y2="16" />
			<line x1="9" y1="19" x2="11" y2="19" />
			<line x1="9" y1="13" x2="11" y2="13" />
			<polyline points="13 6 13 2 23 5 23 23 5 23 5 9 15 9 15 23" />
			<line x1="19" y1="19" x2="19" y2="9" />
		</>
	),
	calendar: (
		<>
			<line x1="2" y1="9" x2="22" y2="9" />
			<line x1="7" y1="1" x2="7" y2="4" />
			<line x1="17" y1="1" x2="17" y2="4" />
			<rect x="2" y="4" width="20" height="16" rx="2" ry="2" />
		</>
	),
	calendarDates: (
		<>
			<line x1="6" y1="1" x2="6" y2="3" />
			<line x1="18" y1="1" x2="18" y2="3" />
			<rect x="5" y="12" width="2" height="1" />
			<rect x="11" y="12" width="2" height="1" />
			<rect x="5" y="17" width="2" height="1" />
			<rect x="11" y="17" width="2" height="1" />
			<rect x="17" y="12" width="2" height="1" />
			<rect data-stroke="none" x="5" y="12" width="2" height="1" fill="currentColor" stroke="none" />
			<rect data-stroke="none" x="11" y="12" width="2" height="1" fill="currentColor" stroke="none" />
			<rect data-stroke="none" x="5" y="17" width="2" height="1" fill="currentColor" stroke="none" />
			<rect data-stroke="none" x="11" y="17" width="2" height="1" fill="currentColor" stroke="none" />
			<rect data-stroke="none" x="17" y="12" width="2" height="1" fill="currentColor" stroke="none" />
			<line x1="1" y1="8" x2="23" y2="8" />
			<rect x="1" y="4" width="22" height="18" rx="2" ry="2" />
		</>
	),
	chart: (
		<>
			<path d="M10 3H14V21H10V3Z" />
			<path d="M18 9H22V21H18V9Z" />
			<path d="M2 15H6V21H2V15Z" />
		</>
	),
	chartBarTrendUp: (
		<>
			<path d="M10 12H14V21H10V12Z" />
			<path d="M18 8H22V21H18V8Z" />
			<path d="M2 16H6V21H2V16Z" />
			<path d="M2 7L6 3L10 7L15 2" />
		</>
	),
	chartDonut: (
		<>
			<line x1="16" y1="12" x2="22" y2="12" />
			<line x1="12" y1="2" x2="12" y2="8" />
			<line x1="9.04" y1="14.687" x2="4.636" y2="18.818" />
			<circle cx="12" cy="12" r="10" />
			<circle cx="12" cy="12" r="4" />
		</>
	),
	chartPieSegment: (
		<>
			<path d="M10 23C14.9706 23 19 18.9706 19 14H10V5C5.02944 5 1 9.02944 1 14C1 18.9706 5.02944 23 10 23Z" />
			<path d="M23 10C23 5.02944 18.9706 1 14 1V10H23Z" />
		</>
	),
	check: (
		<polyline points="3 13 8 19 21 5" />
	),
	chevronDown: (
		<path d="M21 8L12 17L3 8" />
	),
	chevronLeft: (
		<path d="M16 21L7 12L16 3" />
	),
	chevronRight: (
		<path d="M8 21L17 12L8 3" />
	),
	chevronUp: (
		<path d="M21 16L12 7L3 16" />
	),
	circleArrowUp: (
		<>
			<path d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2Z" />
			<path d="M12 17L12 7L12 7.69238" />
			<path d="M16.2427 11.2427L12 7.00004L7.75739 11.2427" />
		</>
	),
	circleCheck: (
		<>
			<circle cx="12" cy="12" r="10" />
			<polyline points="7 13 10 16 17 8" />
		</>
	),
	circleDollar: (
		<>
			<circle cx="12" cy="12" r="11" />
			<line x1="12" y1="5" x2="12" y2="19" />
			<path d=" M15.375,7.219c-1.621-0.891-6.727-1.556-6.727,1.513c0,3.674,6.484,2.377,6.484,5.619S10.918,17.349,8,16.296" />
		</>
	),
	circleExclamation: (
		<>
			<circle cx="12" cy="12" r="10" />
			<line x1="12" y1="7" x2="12" y2="13" />
			<circle cx="12" cy="16.75" r="1.25" fill="currentColor" stroke="none" />
		</>
	),
	circleQuestion: (
		<>
			<circle cx="12" cy="12" r="10" />
			<circle cx="12" cy="17.25" r="1.25" fill="currentColor" stroke="none" />
			<path d="m9.244,8.369c.422-1.608,1.733-2.44,3.201-2.364,1.45.075,2.799.872,2.737,2.722-.089,2.63-2.884,2.273-3.197,4.773h.011" />
		</>
	),
	circleUser: (
		<>
			<circle cx="12" cy="9.5" r="2.5" />
			<path d="m17.917,20.048c-.457-2.86-2.928-5.048-5.917-5.048s-5.46,2.188-5.917,5.048" />
			<circle cx="12" cy="12" r="10" />
		</>
	),
	circleUserSparkle: (
		<>
			<path d="M20.5 3.5L19 0L17.5 3.5L14 5L17.5 6.5L19 10L20.5 6.5L24 5L20.5 3.5Z" fill="currentColor" stroke="none" />
			<path d="M12 13C13.3807 13 14.5 11.8807 14.5 10.5C14.5 9.11929 13.3807 8 12 8C10.6193 8 9.5 9.11929 9.5 10.5C9.5 11.8807 10.6193 13 12 13Z" />
			<path d="M17.7188 20.2046C16.9562 17.7688 14.6875 16 12 16C9.31252 16 7.0438 17.7688 6.28125 20.2046" />
			<path d="M22 12C22 15.3964 20.307 18.3972 17.7188 20.2046C16.0982 21.3362 14.1266 22 12 22C9.87338 22 7.90184 21.3362 6.28125 20.2046C3.69305 18.3972 2 15.3964 2 12C2 6.477 6.477 2 12 2" />
		</>
	),
	circleXmark: (
		<>
			<circle cx="12" cy="12" r="10" />
			<line x1="15.5" y1="8.5" x2="8.5" y2="15.5" />
			<line x1="15.5" y1="15.5" x2="8.5" y2="8.5" />
		</>
	),
	clockRotateClockwise: (
		<>
			<path d="m2,12C2,6.477,6.477,2,12,2s10,4.477,10,10-4.477,10-10,10c-3.535,0-6.641-1.834-8.42-4.602" />
			<polyline points="12 6 12 12 16 15" />
			<polyline points="3 22 3 17 8 17" />
		</>
	),
	copy: (
		<>
			<path d="M10.5 8H19.5C20.3284 8 21 8.67157 21 9.5V20.5C21 21.3284 20.3284 22 19.5 22H10.5C9.67157 22 9 21.3284 9 20.5V9.5C9 8.67157 9.67157 8 10.5 8Z" />
			<path d="M15 4V3.5C15 2.67157 14.3284 2 13.5 2L4.5 2C3.67157 2 3 2.67157 3 3.5L3 14.5C3 15.3284 3.67157 16 4.5 16H5" />
		</>
	),
	crossroad: (
		<>
			<polyline points="12 7 5 7 3 9 5 11 12 11" />
			<polyline points="12 3 19 3 21 5 19 7 12 7" />
			<polyline points="12 11 19 11 21 13 19 15 12 15" />
			<line x1="15" y1="23" x2="9" y2="23" />
			<line x1="12" y1="23" x2="12" y2="1" />
		</>
	),
	cube: (
		<>
			<path d="M12 22.5V11" />
			<path d="M21.5 6.25001L22 6L12 11L2 6.00001L2.5 6.25001" />
			<path d="M22 18.0789V5.92105L12 1.5L2 5.92105V18.0789L12 22.5L22 18.0789Z" />
		</>
	),
	cursor: (
		<polygon points="20,7 2,2 7,20 10,13 18,21 21,18 13,10" />
	),
	database: (
		<>
			<path d="m3,5v14c0,1.7,4,3,9,3s9-1.3,9-3V5" />
			<ellipse cx="12" cy="5" rx="9" ry="3" />
			<path d="m21,12c0,1.7-4,3-9,3s-9-1.3-9-3" />
		</>
	),
	diploma: (
		<>
			<path d="M8,19v1.5 C8,22.75,5.5,23,5.5,23s14.829,0,15,0c1.381,0,2.5-1.119,2.5-2.5V19H8z" />
			<path d="M13,1H3v19.5 C3,21.881,4.119,23,5.5,23H7" />
			<polyline points="22,7.646 22,15 19,13 16,15 16,7.646" />
			<circle cx="19" cy="5" r="4" />
		</>
	),
	doorOpen: (
		<>
			<path d="M4 2H20V19H18.0909" />
			<path d="M14 14H12V15H14" />
			<path d="M14 22.5V5.84483L4.11089 2H4V18.6552L14 22.5Z" />
		</>
	),
	dotsVertical: (
		<>
			<circle cx="12" cy="12" r=".75" fill="currentColor" />
			<circle cx="12" cy="3.75" r=".75" fill="currentColor" />
			<circle cx="12" cy="20.25" r=".75" fill="currentColor" />
		</>
	),
	doubleChevronRight: (
		<>
			<path d="M11 3L20 12L11 21" />
			<path d="M4 3L13 12L4 21" />
		</>
	),
	doubleChevronLeft: (
		<>
			<path d="M13 3L4 12L13 21" />
			<path d="M20 3L11 12L20 21" />
		</>
	),
	download: (
		<>
			<path d="m3,16v3c0,1.105.895,2,2,2h14c1.105,0,2-.895,2-2v-3" />
			<polyline points="12 3 12 15 12 14" />
			<polyline points="7 10 12 15 17 10" />
		</>
	),
	envelope: (
		<>
			<polyline points="2 8 12 13 22 8" />
			<rect x="2" y="4" width="20" height="16" rx="2" ry="2" />
		</>
	),
	expand: (
		<>
			<path d="M3 8V5C3 3.89543 3.89543 3 5 3H8" />
			<path d="M16 3H19C20.1046 3 21 3.89543 21 5V8" />
			<path d="M21 16V19C21 20.1046 20.1046 21 19 21H16" />
			<path d="M8 21H5C3.89543 21 3 20.1046 3 19V16" />
		</>
	),
	fileXmark: (
		<>
			<path d="M20 11V4C20 2.89543 19.1046 2 18 2H11.0784C10.548 2 10.0393 2.21071 9.66421 2.58579L4.58579 7.66421C4.21071 8.03929 4 8.54799 4 9.07843L4 20C4 21.1046 4.89543 22 6 22H9" />
			<path d="M4 9H11V2" />
			<path d="M13.5 22.5L20.5 15.5" />
			<path d="M13.5 15.5L20.5 22.5" />
		</>
	),
	file: (
		<>
			<path d="M11.0784 2H18C19.1046 2 20 2.89543 20 4V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V9.07843C4 8.54799 4.21071 8.03929 4.58579 7.66421L9.66421 2.58579C10.0393 2.21071 10.548 2 11.0784 2Z" />
			<path d="M4 9H11V2" />
			<path d="M8 17H16" />
			<path d="M8 13L11 13" />
		</>
	),
	filter: (
		<polygon points="2,2 22,2 14,14 14,22 10,22 10,14 " />
	),
	flag: (
		<>
			<path d="M4 4L5.84462 3.26215C7.83264 2.46694 10.0781 2.64687 11.9141 3.74849V3.74849C13.2635 4.55807 14.8503 4.87853 16.4081 4.65599L21 4L18 10L21 16L16.4081 16.656C14.8503 16.8785 13.2635 16.5581 11.9141 15.7485V15.7485C10.0781 14.6469 7.83264 14.4669 5.84462 15.2622L4 16" />
			<path d="M4 22V2" />
		</>
	),
	graduationCap: (
		<>
			<line x1="23" y1="11" x2="23" y2="16" />
			<polygon points="12,13 2,8 12,3 22,8" />
			<path d="M5,9.5V18c0,1.657,3.134,3,7,3 s7-1.343,7-3V9.5" />
		</>
	),
	gridCheck: (
		<>
			<rect x="3" y="3" width="7" height="7" rx="1" ry="1" />
			<rect x="14" y="14" width="7" height="7" rx="1" ry="1" />
			<rect x="3" y="14" width="7" height="7" rx="1" ry="1" />
			<polyline points="14.25 6.75 16.5 9 22 3.5" />
		</>
	),
	idBadge: (
		<>
			<path d="M17 4H20C21.105 4 22 4.895 22 6V18C22 19.105 21.105 20 20 20H4C2.895 20 2 19.105 2 18V6C2 4.895 2.895 4 4 4H7" />
			<path d="M15 11H18" />
			<path d="M16 15H18" />
			<path d="M12 1C12.552 1 13 1.448 13 2V6H11V2C11 1.448 11.448 1 12 1Z" />
			<path d="M9 11C9.55228 11 10 10.5523 10 10C10 9.44772 9.55228 9 9 9C8.44772 9 8 9.44772 8 10C8 10.5523 8.44772 11 9 11Z" fill="currentColor" />
			<path d="M11.816 16C11.403 14.837 10.304 14 8.99999 14C7.69599 14 6.59699 14.837 6.18399 16H11.816Z" fill="currentColor" />
		</>
	),
	laptopChartCols: (
		<>
			<path d="M3 13V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V13" />
			<path d="M12 7V13" />
			<path d="M16 9V13" />
			<path d="M8 11V13" />
			<path d="M1 17V19C1 20.1046 1.89543 21 3 21H21C22.1046 21 23 20.1046 23 19V17H16C16 17.5523 15.5523 18 15 18H9C8.44772 18 8 17.5523 8 17H1Z" />
		</>
	),
	laptopCheck: (
		<>
			<path d="M3 13V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V13" />
			<path d="M1 17V19C1 20.1046 1.89543 21 3 21H21C22.1046 21 23 20.1046 23 19V17H16C16 17.5523 15.5523 18 15 18H9C8.44772 18 8 17.5523 8 17H1Z" />
			<path d="M8 10L11 13L16.5 7.5" />
		</>
	),
	laptopSync: (
		<>
			<path d="M3 13V5C3 3.89543 3.89543 3 5 3H10" />
			<path d="M1 17V19C1 20.1046 1.89543 21 3 21H21C22.1046 21 23 20.1046 23 19V17H16C16 17.5523 15.5523 18 15 18H9C8.44772 18 8 17.5523 8 17H1Z" />
			<path d="M23 3V6.5H19.5" />
			<path d="M13 12V8.5H16.5" />
			<path d="M21.854 6.5H22.242C21.788 4.496 19.996 3 17.854 3C16.5214 3 15.3241 3.57941 14.5 4.5" />
			<path d="M14.146 8.5H13.758C14.212 10.504 16.004 12 18.146 12C19.4786 12 20.6759 11.4206 21.5 10.5" />
		</>
	),
	lightbulb: (
		<>
			<path d="M20,9c0-4.418-3.582-8-8-8 S4,4.582,4,9c0,3.357,2.069,6.226,5,7.413V19h6v-2.587C17.931,15.226,20,12.357,20,9z" />
			<polyline points="15,19 15,23 9,23 9,19" />
			<path d="M8,9 c0-2.209,1.791-4,4-4" />
		</>
	),
	listCheck: (
		<>
			<line x1="12" y1="6" x2="23" y2="6" />
			<rect x="2" y="3" width="6" height="6" />
			<line x1="12" y1="18" x2="23" y2="18" />
			<polyline points="2 18 4 20 10 14" />
		</>
	),
	loader: (
		<>
			<path opacity="0.4" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" />
			<path d="M22 12C22 6.47715 17.5228 2 12 2" />
		</>
	),
	locationDot: (
		<>
			<path d="M20,9c0,4.9-8,13-8,13 S4,13.9,4,9c0-5.1,4.1-8,8-8S20,3.9,20,9z" />
			<circle cx="12" cy="9" r="3" />
		</>
	),
	magicWandSparkle: (
		<>
			<path d="M3 21L13 11L12.5 11.5" />
			<path d="M19.5 4.5L18.2506 8.37138L20.7716 11.7137L16.6574 11.5853L14.3438 14.9001L13.0505 10.9495L9.09988 9.65622L12.4147 7.34263L12.2863 3.22843L15.6286 5.74943L19.5 4.5Z" />
			<path d="M6.55 3.45L5.5 1L4.45 3.45L2 4.5L4.45 5.55L5.5 8L6.55 5.55L9 4.5L6.55 3.45Z" fill="currentColor" stroke="none" />
			<path d="M20.55 17.45L19.5 15L18.45 17.45L16 18.5L18.45 19.55L19.5 22L20.55 19.55L23 18.5L20.55 17.45Z" fill="currentColor" stroke="none" />
		</>
	),
	messages: (
		<>
			<path d="m16,2H4c-1.105,0-2,.895-2,2v8c0,1.105.895,2,2,2h1v5l7-5h4c1.105,0,2-.895,2-2V4c0-1.105-.895-2-2-2Z" />
			<path d="m22,7v9c0,1.105-.895,2-2,2h-1s0,4,0,4l-5-4h-2" />
		</>
	),
	messageText: (
		<>
			<line x1="8" y1="10" x2="16" y2="10" />
			<line x1="8" y1="14" x2="13" y2="14" />
			<path d="m12,3C6.477,3,2,7.029,2,12c0,1.843.618,3.556,1.675,4.983l-1.224,4.566,5.287-1.417c1.294.551,2.735.868,4.262.868,5.523,0,10-4.029,10-9S17.523,3,12,3Z" />
		</>
	),
	news: (
		<>
			<line x1="9" y1="14" x2="19" y2="14" />
			<line x1="9" y1="18" x2="19" y2="18" />
			<rect x="9" y="6" width="10" height="4" />
			<path d="M3,23a2.006,2.006,0,0,1-2-2V13H2" />
			<path d="M5,1V21a2.006,2.006,0,0,1-2,2H21a2.006,2.006,0,0,0,2-2V1Z" />
		</>
	),
	offline: (
		<>
			<path d="M17.273,6.727A7.451,7.451,0,0,0,11.5,4,7.551,7.551,0,0,0,4,11.1,4.015,4.015,0,0,0,5,19" />
			<path d="M10,19h9a4,4,0,0,0,0-8,7.642,7.642,0,0,0-.267-1.491" />
			<line x1="22" y1="2" x2="2" y2="22" />
		</>
	),
	organization: (
		<>
			<path d="M4,7h0c-1.105,0-2-.895-2-2h0c0-1.105,.895-2,2-2h0c1.105,0,2,.895,2,2h0c0,1.105-.895,2-2,2Z" />
			<path d="M6,21H3v-4H1v-5c0-1.105,.895-2,2-2h1" />
			<path d="M20,7h0c1.105,0,2-.895,2-2h0c0-1.105-.895-2-2-2h0c-1.105,0-2,.895-2,2h0c0,1.105,.895,2,2,2Z" />
			<path d="M18,21h3v-4h2v-5c0-1.105-.895-2-2-2h-1" />
			<path d="M12,6h0c-1.381,0-2.5-1.119-2.5-2.5h0c0-1.381,1.119-2.5,2.5-2.5h0c1.381,0,2.5,1.119,2.5,2.5h0c0,1.381-1.119,2.5-2.5,2.5Z" />
			<path d="M15,23h-6v-6h-2v-6c0-1.105,.895-2,2-2h6c1.105,0,2,.895,2,2v6h-2v6Z" />
		</>
	),
	page: (
		<>
			<rect x="3" y="1" width="18" height="22" />
			<rect x="7" y="5" width="10" height="6" />
			<line x1="7" y1="15" x2="17" y2="15" />
			<line x1="7" y1="19" x2="17" y2="19" />
		</>
	),
	palette: (
		<>
			<path d="M1,12c0-3.7,1.8-7,4.7-9 c1-0.7,2.7-0.9,3.3,0c1,1.2-0.8,3,0,4c2,2.4,6.1-2.3,11,0c3.5,1.8,3,6.3,2.6,8c-1.3,4.6-5.6,8-10.6,8C5.9,23,1,18.1,1,12z" />
			<circle cx="17" cy="12" r="2" />
			<circle cx="5" cy="12" r="1" />
			<circle cx="7.5" cy="17" r="1" />
			<circle cx="13" cy="19" r="1" />
		</>
	),
	paperPlane: (
		<>
			<polyline points="22,2 7,14 7,21 10.6,16.7" />
			<polygon points="2,10 22,2 18,22" />
		</>
	),
	pencil: (
		<>
			<line x1="14" y1="5" x2="19" y2="10" />
			<path d="M9,20l-7,2,2-7L16.414,2.586c.781-.781,2.047-.781,2.828,0l2.172,2.172c.781,.781,.781,2.047,0,2.828l-12.414,12.414Z" />
		</>
	),
	percentageArrowUp: (
		<>
			<path d="M8.0625 2L2.5625 8.33333L6 8.33333L6 21L10.125 21L10.125 8.33333L13.5625 8.33333L8.0625 2Z" />
			<path d="M15.5 15C16.3284 15 17 14.3284 17 13.5C17 12.6716 16.3284 12 15.5 12C14.6716 12 14 12.6716 14 13.5C14 14.3284 14.6716 15 15.5 15Z" />
			<path d="M21.5 21C22.3284 21 23 20.3284 23 19.5C23 18.6716 22.3284 18 21.5 18C20.6716 18 20 18.6716 20 19.5C20 20.3284 20.6716 21 21.5 21Z" />
			<path d="M15 20L22 13" />
		</>
	),
	pointer: (
		<path d="M21.1666 9.16667L4.1666 4.16667L9.1666 21.1667L12.9999 13L21.1666 9.16667Z" />
	),
	rectLogin: (
		<>
			<path d="M8 4V4C8 2.89543 8.89543 2 10 2L18 2C19.1046 2 20 2.89543 20 4L20 20C20 21.1046 19.1046 22 18 22L10 22C8.89543 22 8 21.1046 8 20V20" />
			<path d="M3 12L15 12L14.5 12" />
			<path d="M10.7573 16.2427L15 12L10.7573 7.75739" />
		</>
	),
	rectLogout: (
		<>
			<path d="M16 4V4C16 2.89543 15.1046 2 14 2L6 2C4.89543 2 4 2.89543 4 4L4 20C4 21.1046 4.89543 22 6 22L14 22C15.1046 22 16 21.1046 16 20V20" />
			<path d="M9.99999 12L21.5 12L21 12" />
			<path d="M17.2574 16.2427L21.5 12L17.2574 7.75739" />
		</>
	),
	rulerPencil: (
		<>
			<line x1="16" y1="5" x2="22" y2="5" />
			<polygon points="22,18 19,22 16,18 16,1 22,1" />
			<line x1="2" y1="6" x2="5" y2="6" />
			<line x1="2" y1="10" x2="4" y2="10" />
			<line x1="2" y1="14" x2="5" y2="14" />
			<line x1="2" y1="18" x2="4" y2="18" />
			<rect x="2" y="1" width="8" height="22" />
			</>
	),
	search: (
		<>
			<line x1="22" y1="22" x2="15.656" y2="15.656" />
			<circle cx="10" cy="10" r="8" />
		</>
	),
	aiSearch: (
		<>
			<path
				d="M23.1902 3.71925L21.3768 3.42571C20.9635 3.359 20.6568 3.03878 20.5768 2.62516L20.2835 0.810563C20.1102 -0.270188 18.5635 -0.270188 18.3902 0.810563L18.0968 2.62516C18.0302 3.03878 17.7101 3.34566 17.2968 3.42571L15.4835 3.71925C14.4035 3.8927 14.4035 5.44045 15.4835 5.6139L17.2968 5.90744C17.7101 5.97415 18.0168 6.29437 18.0968 6.70799L18.3902 8.52259C18.5635 9.60334 20.1102 9.60334 20.2835 8.52259L20.5768 6.70799C20.6435 6.29437 20.9635 5.98749 21.3768 5.90744L23.1902 5.6139C24.2702 5.44045 24.2702 3.8927 23.1902 3.71925Z"
				fill="currentColor"
				stroke="none"
			/>
			<path
				d="M18.6701 12.005C17.9368 12.005 17.3368 12.6054 17.3368 13.3393C17.3368 17.0218 14.3501 20.0106 10.6701 20.0106C6.99005 20.0106 4.00336 17.0218 4.00336 13.3393C4.00336 9.65671 6.99005 6.66797 10.6701 6.66797C11.4034 6.66797 12.0034 6.06755 12.0034 5.33371C12.0034 4.59987 11.4034 3.99945 10.6701 3.99945C5.52337 3.99945 1.33668 8.18903 1.33668 13.3393C1.33668 15.4474 2.04335 17.3954 3.23002 18.9565C3.17669 18.9965 3.11002 19.0232 3.05669 19.0632L0.390003 21.7318C-0.130001 22.2521 -0.130001 23.0927 0.390003 23.6131C0.656671 23.8799 0.990007 24 1.33668 24C1.68334 24 2.01668 23.8666 2.28335 23.6131L4.95003 20.9446C4.95003 20.9446 5.0167 20.8245 5.0567 20.7711C6.61671 21.9586 8.56339 22.6657 10.6701 22.6657C15.8168 22.6657 20.0035 18.4762 20.0035 13.3259C20.0035 12.5921 19.4035 11.9917 18.6701 11.9917V12.005Z"
				fill="currentColor"
				stroke="none"
			/>
		</>
	),
	sliders: (
		<>
			<line x1="19" y1="12" x2="21" y2="12" />
			<line x1="11" y1="5" x2="21" y2="5" />
			<line x1="3" y1="5" x2="5" y2="5" />
			<line x1="3" y1="12" x2="13" y2="12" />
			<line x1="11" y1="19" x2="21" y2="19" />
			<line x1="3" y1="19" x2="5" y2="19" />
			<circle cx="8" cy="5" r="3" />
			<circle cx="16" cy="12" r="3" />
			<circle cx="8" cy="19" r="3" />
		</>
	),
	trash: (
		<>
			<path d="m18.833,8l-.503,12.083c-.045,1.071-.926,1.917-1.998,1.917H7.668c-1.072,0-1.954-.845-1.998-1.917l-.503-12.083" />
			<path d="m10,4v-1c0-.552.448-1,1-1h2c.552,0,1,.448,1,1v1" />
			<line x1="10" y1="18" x2="10" y2="12" />
			<line x1="14" y1="18" x2="14" y2="12" />
			<path d="m5,4h14c1.104,0,2,.896,2,2v2H3v-2c0-1.104.896-2,2-2Z" />
		</>
	),
	send: (
		<path d="m15.5 21.5 6-19-19 6 9 4zM11.5 12.5l10-10" />
	),
	settings: (
		<>
			<circle cx="12" cy="12" r="3" />
			<path d="m22,13.25v-2.5l-2.318-.966c-.167-.581-.395-1.135-.682-1.654l.954-2.318-1.768-1.768-2.318.954c-.518-.287-1.073-.515-1.654-.682l-.966-2.318h-2.5l-.966,2.318c-.581.167-1.135.395-1.654.682l-2.318-.954-1.768,1.768.954,2.318c-.287.518-.515,1.073-.682,1.654l-2.318.966v2.5l2.318.966c.167.581.395,1.135.682,1.654l-.954,2.318,1.768,1.768,2.318-.954c.518.287,1.073.515,1.654.682l.966,2.318h2.5l.966-2.318c.581-.167,1.135-.395,1.654-.682l2.318.954,1.768-1.768-.954-2.318c.287-.518.515-1.073.682-1.654l2.318-.966Z" />
		</>
	),
	share: (
		<>
			<line x1="7.6" y1="10.5" x2="16.4" y2="5.5" />
			<line x1="7.6" y1="13.5" x2="16.4" y2="18.5" />
			<circle cx="5" cy="12" r="3" />
			<circle cx="19" cy="4" r="3" />
			<circle cx="19" cy="20" r="3" />
		</>
	),
	sidebar: (
		<>
			<rect x="1" y="3" width="22" height="18" rx="2" ry="2" />
			<line x1="13" y1="3" x2="13" y2="21" />
			<line x1="17" y1="8" x2="19" y2="8" />
			<line x1="17" y1="12" x2="19" y2="12" />
			<line x1="17" y1="16" x2="19" y2="16" />
		</>
	),
	sparkle: (
		<path d="M12,2l3.2,6.8,6.8,3.2-6.8,3.2-3.2,6.8-3.2-6.8-6.8-3.2,6.8-3.2s3.2-6.8,3.2-6.8Z" />
	),
	sparkleSolid: (
		<path
			d="M12,2l3.2,6.8,6.8,3.2-6.8,3.2-3.2,6.8-3.2-6.8-6.8-3.2,6.8-3.2s3.2-6.8,3.2-6.8Z"
			fill="currentColor"
		/>
	),
	sparkles: (
		<>
			<path d="M12 4L14.5263 9.47368L20 12L14.5263 14.5263L12 20L9.47368 14.5263L4 12L9.47368 9.47368L12 4Z" />
			<path
				d="M5.55 3.45L4.5 1L3.45 3.45L1 4.5L3.45 5.55L4.5 8L5.55 5.55L8 4.5L5.55 3.45Z"
				fill="currentColor"
				stroke="none"
			/>
		</>
	),
	star: (
		<>
			<polygon points="12 2.245 15.039 8.403 21.836 9.39 16.918 14.185 18.079 20.954 12 17.759 5.921 20.954 7.082 14.185 2.164 9.39 8.961 8.403 12 2.245" />
		</>
	),
	target: (
		<>
			<circle cx="12" cy="12" r="11" />
			<circle cx="12" cy="12" r="7" />
			<circle cx="12" cy="12" r="3" />
		</>
	),
	targetArrow: (
		<>
			<path d="M18.945,13 C18.981,13.328,19,13.662,19,14c0,4.971-4.029,9-9,9s-9-4.029-9-9s4.029-9,9-9c0.338,0,0.672,0.019,1,0.055" />
			<circle cx="10" cy="14" r="5" />
			<line x1="19" y1="5" x2="22" y2="2" />
			<line x1="10" y1="14" x2="16" y2="8" />
			<polygon points="19,5 18,2 15,5 16,8 19,9 22,6" />
			<polygon points="19,5 18,2 15,5 16,8 19,9 22,6" />
		</>
	),
	thumbsUp: (
		<>
			<polyline points="6 23 1 23 1 12 6 12" />
			<path d="M6,12,9,1H9a3,3,0,0,1,3,3v6h7.5a3,3,0,0,1,2.965,3.456l-1.077,7A3,3,0,0,1,18.426,23H6Z" />
		</>
	),
	thumbsUpSolid: (
		<>
			<path d="M4,11H1a1,1,0,0,0-1,1V23a1,1,0,0,0,1,1H4Z" fill="currentColor" stroke="none" />
			<path
				d="M22.539,10.4A4,4,0,0,0,19.5,9H13V4c0-2.206-.794-4-3-4a1,1,0,0,0-.965.737L6,11V24H18.426a3.979,3.979,0,0,0,3.954-3.392l1.076-7A4,4,0,0,0,22.539,10.4Z"
				fill="currentColor"
				stroke="none"
			/>
		</>
	),
	thumbsDown: (
		<>
			<polyline points="6 1 1 1 1 12 6 12" />
			<path d="M6,12,9,23H9a3,3,0,0,0,3-3V14h7.5a3,3,0,0,0,2.965-3.456l-1.077-7A3,3,0,0,0,18.426,1H6Z" />
		</>
	),
	thumbsDownSolid: (
		<>
			<path d="M4,13H1a1,1,0,0,1-1-1V1A1,1,0,0,1,1,0H4Z" fill="currentColor" stroke="none" />
			<path
				d="M22.539,13.605A4,4,0,0,1,19.5,15H13v5c0,2.206-.794,4-3,4a1,1,0,0,1-.965-.737L6,13V0H18.426A3.979,3.979,0,0,1,22.38,3.392l1.076,7A4,4,0,0,1,22.539,13.605Z"
				fill="currentColor"
				stroke="none"
			/>
		</>
	),
	triangleExclamation: (
		<>
			<circle cx="12" cy="16.75" r="1.25" fill="currentColor" stroke="none" />
			<line x1="12" y1="13" x2="12" y2="9" />
			<path d="m10.171,4.06l-7.899,13.783c-.806,1.406.209,3.157,1.829,3.157h15.798c1.62,0,2.635-1.751,1.829-3.157l-7.899-13.783c-.81-1.413-2.849-1.413-3.659,0Z" />
		</>
	),
	user: (
		<>
			<circle cx="12" cy="6" r="4" />
			<path d="m12,13c-4.418,0-8,3.582-8,8,5.333,1.333,10.667,1.333,16,0,0-4.418-3.582-8-8-8Z" />
		</>
	),
	userBubble: (
		<>
			<path d="M20 4H4C2.89543 4 2 4.89543 2 6V16C2 17.1046 2.89543 18 4 18H9L12 22L15 18H20C21.1046 18 22 17.1046 22 16V6C22 4.89543 21.1046 4 20 4Z" />
			<path d="M12 11C13.3807 11 14.5 9.88071 14.5 8.5C14.5 7.11929 13.3807 6 12 6C10.6193 6 9.5 7.11929 9.5 8.5C9.5 9.88071 10.6193 11 12 11Z" fill="currentColor" stroke="none" />
			<path d="M12 12C9.47799 12 7.38076 13.6986 6.73029 16H17.2697C16.6192 13.6986 14.522 12 12 12Z" fill="currentColor" stroke="none" />
		</>
	),
	userGraduate: (
		<>
			<path d="M16.27,6.27c.1.35.15.72.15,1.1,0,2.38-1.98,4.31-4.41,4.31s-4.41-1.93-4.41-4.31c0-.38.05-.75.15-1.1" />
			<polygon points="12 6.9 3.17 3.95 12 1 20.83 3.95 12 6.9"/>
			<path d="M20.83,23c0-3.47-2.11-6.46-5.14-7.82l-3.68,2.52-3.68-2.52c-3.03,1.36-5.14,4.35-5.14,7.82h17.65Z"/>
			<line x1="4.57" y1="4.74" x2="4.57" y2="10.37"/>
		</>
	),
	users: (
		<>
			<circle cx="16.5" cy="4.75" r="2.75" />
			<path d="m15.553,16.5h6.422c.015-.165.025-.331.025-.5,0-3.038-2.462-5.5-5.5-5.5-.825,0-1.604.187-2.306.512" />
			<circle cx="7.5" cy="10.25" r="2.75" />
			<path d="m12.975,22c.015-.165.025-.331.025-.5,0-3.038-2.462-5.5-5.5-5.5s-5.5,2.462-5.5,5.5c0,.169.01.335.025.5h10.95Z" />
		</>
	),
	usersFemaleMale: (
		<>
			<path d="M2.73799 19.101C1.70899 19.396 0.998993 20.337 0.998993 21.408V21.999H16.999V21.408C16.999 20.337 16.29 19.396 15.26 19.101L12.725 18.375C11.798 18.116 11.158 18.028 11.158 17.085V16C12.532 15.888 13.44 15.389 14.344 14.558C12.944 12.983 12.998 11.2 12.998 10C12.998 7.791 11.207 6 8.99799 6C6.78899 6 4.99799 7.791 4.99799 10C4.99799 11.2 5.05199 12.983 3.65199 14.558C4.55699 15.388 5.46399 15.8 6.83799 15.912V17.085C6.83799 18.028 6.19899 18.116 5.27099 18.375L2.73799 19.101Z" />
			<path d="M13 2.733C13.712 2.271 14.569 2 15.5 2C18.303 2 20.435 4.463 19.975 7.168L19.509 9.905C19.321 11.006 18.682 11.938 17.796 12.532V13.778C17.796 14.564 19.506 15.282 21.012 15.798C22.202 16.206 23.001 17.286 23.001 18.543V19.999H21.001" />
		</>
	),
	usersSmallLarge: (
		<>
			<circle cx="7" cy="11" r="3" />
			<circle cx="16" cy="5" r="4" />
			<path d="M17,23h6v-4c0-3.866-3.134-7-7-7-1.074,0-2.091,.242-3,.674" />
			<path d="M7,17c-3.314,0-6,2.686-6,6H13c0-3.314-2.686-6-6-6Z" />
		</>
	),
	video: (
		<>
			<path d="M21,2a2,2,0,0,1,2,2V20a2,2,0,0,1-2,2H3a2,2,0,0,1-2-2V4A2,2,0,0,1,3,2Z" />
			<polygon points="10 8 16 12 10 16 10 8" />
		</>
	),
	videoStack: (
		<>
			<rect x="5" y="3" width="18" height="14" rx="2" />
			<path d="M1,7V19a2,2,0,0,0,2,2H19" />
			<polygon points="12 7 17 10 12 13 12 7" />
		</>
	),
	wifiOff: (
		<>
			<path d="M1.42468 9.36218C4.13664 6.66606 7.87374 5 12 5C14.0663 5 16.0351 5.41782 17.8264 6.17359L18.5 5.5" />
			<path d="M17.7952 11.8494C18.2503 12.1735 18.677 12.5348 19.0711 12.9289" />
			<path d="M21.3881 8.30042C21.8082 8.63796 22.2097 8.99767 22.5909 9.37776" />
			<path d="M4.92896 12.9289C6.7386 11.1193 9.2386 10 12 10C12.6259 10 13.2384 10.0575 13.8325 10.1675L14 10" />
			<path d="M15.5355 16.4645L15.5327 16.4616M15.5327 16.4616L12 19.9943L10.824 18.8183M15.5327 16.4616C15.1412 16.0707 14.6852 15.7444 14.182 15.5" />
			<path d="M3 21L21 3" />
</>
	),
	windowCursor: (
		<>
			<path d="M2 8H22" />
			<path d="M5.5 20L4 20C2.89543 20 2 19.1046 2 18L2 6C2 4.89543 2.89543 4 4 4L20 4C21.1046 4 22 4.89543 22 6L22 18C22 19.1046 21.1046 20 20 20L18.5 20" />
			<path d="M8.4641 11L15.6782 15.4952L13.08 16.9953L15.3301 20.8923L13.5981 21.8923L11.348 17.9953L8.75 19.4952L8.4641 11Z" fill="currentColor" stroke="none" />
		</>
	),
	xmark: (
		<>
			<line x1="20" y1="20" x2="4" y2="4" />
			<line x1="20" y1="4" x2="4" y2="20" />
		</>
	),

	// uConnect logo
	uconnect: (
		<path d="M24,12C24,5.37,18.63,0,12,0S0,5.37,0,12s5.37,12,12,12h12l-3.51-3.51c2.17-2.17,3.51-5.17,3.51-8.49ZM17.44,18.19h-3.05v-1.5h-.07c-.64,1.16-1.9,1.72-3.35,1.72-3.02,0-4.42-1.93-4.42-4.83v-7.45h3.05v7.39c0,1.7.8,2.49,2.09,2.49,1.81,0,2.7-1.38,2.7-3.44v-6.43h3.05v12.05Z" fillRule="evenodd" fill="currentColor" stroke="none" />
	),

	// Vendor logos
	'12twenty': (
		<path
			d="M12,0C5.37,0,0,5.37,0,12s5.37,12,12,12,12-5.37,12-12S18.63,0,12,0ZM12.98,20.69c-1.28.14-2.58,0-3.8-.42-1.22-.42-2.33-1.1-3.26-2.01-.93-.9-1.64-2-2.09-3.21-.45-1.21-.63-2.5-.52-3.79.11-1.29.5-2.53,1.14-3.65.65-1.12,1.53-2.08,2.59-2.81,1.06-.74,2.27-1.23,3.55-1.44v3.81c-.66.2-1.28.53-1.8.97-.53.44-.96.99-1.27,1.61-.31.62-.49,1.29-.52,1.98-.04.69.07,1.38.31,2.02.24.65.61,1.24,1.09,1.73.48.5,1.05.89,1.69,1.16.64.27,1.32.4,2.01.39s1.37-.16,2-.45h0l2.51,2.87c-1.1.68-2.34,1.1-3.62,1.24ZM18.74,17.58l-2.51-2.87c.42-.66.68-1.4.77-2.18.08-.78-.02-1.56-.29-2.29-.27-.73-.71-1.39-1.28-1.92-.57-.53-1.26-.92-2.01-1.15,0,0,0-3.81,0-3.81,1.52.25,2.95.9,4.14,1.87,1.19.98,2.1,2.25,2.64,3.7.54,1.44.69,3,.43,4.52s-.91,2.94-1.89,4.13Z"
			fill="currentColor"
			stroke="none"
		/>
	),
	anthology: (
		<polygon
			points="9.56 2.2 2.15 22.2 7.62 22.2 11.74 9.73 11.86 9.73 15.98 22.2 21.45 22.2 14.04 2.2 9.56 2.2"
			fill="currentColor"
			stroke="none"
		/>
	),
	campusgroups: (
		<path
			d="M22.67,6.98c-1.75-3.31-4.95-5.61-8.65-6.21,0,0-.02,0-.02,0-.47-.06-.9.26-.96.73-.06.47.26.9.73.96h.02c5.65.77,9.61,5.97,8.84,11.62-.58,4.26-3.73,7.72-7.93,8.68-1.57.36-3.19.35-4.76-.02-1.56-.37-3.02-1.11-4.25-2.14-.11-.09-.27-.08-.37.03-.1.11-.09.28.02.37,0,0,0,0,0,0h0c1.27,1.14,2.79,1.95,4.43,2.4,3.4.91,7.03.18,9.8-1.99,2.85-2.21,4.48-5.63,4.41-9.23-.03-1.81-.49-3.59-1.33-5.19h0ZM12.31,21.97c5.11-.02,9.24-4.18,9.23-9.3-.02-5.11-4.18-9.24-9.3-9.23-5.1.02-9.23,4.16-9.23,9.26.01,5.12,4.17,9.27,9.3,9.26ZM15.04,9.43c1.2,0,1.93.32,2.62.91l-.93,1.11c-.52-.43-.97-.67-1.75-.67-1.07,0-1.92.94-1.92,2.07v.02c0,1.22.84,2.11,2.02,2.11.49.01.98-.12,1.38-.4v-.95h-1.48v-1.26h2.9v2.89c-.79.68-1.81,1.06-2.85,1.06-2.08,0-3.51-1.45-3.51-3.42v-.02c0-1.89,1.48-3.44,3.5-3.44,0,0,0,0,0,0ZM6.17,12.87c0-1.89,1.43-3.44,3.48-3.44,1.09,0,1.8.31,2.38.79-.33.36-.58.78-.76,1.24-.49-.43-1-.69-1.63-.69-1.13,0-1.94.93-1.94,2.07v.02c0,1.14.79,2.09,1.94,2.09.7,0,1.15-.25,1.62-.66.15.44.39.84.69,1.19-.6.51-1.3.82-2.36.82-1.86.03-3.39-1.45-3.43-3.3,0-.04,0-.08,0-.12v-.02h0s0,0,0,0ZM9.63,2.25c.04.27.29.46.56.43l1.29-.17c.27-.04.46-.29.43-.56h0l-.17-1.29c-.04-.27-.29-.46-.56-.42l-1.29.17c-.27.04-.46.29-.43.56l.17,1.29h0ZM6.44,3.64c.12.24.42.34.66.21l1.15-.59c.24-.12.34-.42.21-.66,0,0,0,0,0,0l-.59-1.14c-.12-.24-.42-.33-.66-.21l-1.15.59c-.24.12-.34.42-.21.66,0,0,0,0,0,0l.59,1.15h0ZM3.8,6.08c.2.19.5.18.69-.02l.89-.93c.19-.19.18-.5-.01-.69,0,0,0,0,0,0l-.93-.88c-.2-.19-.5-.18-.69.02l-.89.92c-.19.19-.18.5.01.69,0,0,0,0,0,0l.93.88h0ZM.92,8.71l1.17.53c.25.11.54,0,.65-.24l.53-1.17c.11-.25,0-.53-.24-.64l-1.17-.53c-.25-.11-.54,0-.65.24l-.53,1.17c-.11.25,0,.53.24.64,0,0,0,0,0,0h0ZM1.62,12.46c.25.02.47-.17.49-.42l.08-1.19c.02-.25-.17-.47-.42-.48l-1.19-.08c-.25-.02-.47.17-.49.42L0,11.89c-.02.25.17.47.42.48l1.19.08h0ZM2.25,15.01l-.2-1.05c-.04-.22-.25-.37-.48-.33l-1.06.2c-.22.04-.37.25-.33.48l.2,1.05c.04.22.25.37.48.33l1.06-.2c.22-.04.37-.25.33-.47h0,0ZM3.26,17.57l-.46-.86c-.1-.18-.32-.25-.5-.15l-.86.46c-.18.1-.25.32-.15.5,0,0,0,0,0,0l.46.86c.1.18.32.25.5.15l.86-.46c.18-.1.25-.32.16-.5h0s0,0,0,0h0ZM3.95,18.89c-.12-.13-.31-.14-.44-.02l-.61.55c-.13.11-.14.31-.02.44,0,0,0,0,0,0l.55.61c.12.13.31.14.44.02l.61-.55c.13-.11.14-.31.03-.44,0,0,0,0,0,0l-.55-.61h0Z"
			fill="currentColor"
			stroke="none"
		/>
	),
	firsthand: (
		<path
			d="M19.08,2.44l-.95.97,1.05,1.03c-8.03.33-13.12,3.34-14.5,6.16-.55,1.12-.5,2.18.13,3,.42.54,1.05.92,1.87,1.15-1.12,1.85-1.91,4.33-2.04,7.64l1.35.05h0c.14-3.45,1.02-5.83,2.14-7.46,1.08.07,2.36-.04,3.81-.34,3.65-.74,5.49-1.83,5.33-3.14-.12-.98-1.33-1.57-3.07-1.5-1.92.08-4.64,1.01-6.69,3.54-.84-.15-1.38-.44-1.64-.78-.39-.5-.21-1.13.01-1.58,1.02-2.08,5.44-5.12,13.51-5.4l-1.06,1.31,1.06.85,2.33-2.9-2.65-2.61ZM12.8,11.59c.65-.18,1.22-.24,1.69-.24h0c.78,0,1.28.18,1.43.32-.16.27-1.15,1.02-4.24,1.65-.92.19-1.72.28-2.41.32,1.29-1.26,2.67-1.81,3.53-2.04Z"
			fill="currentColor"
			stroke="none"
		/>
	),
	forage: (
		<path
			d="M5.98,3.03c.29-.25.66-.4,1.04-.4,3.34,0,6.69,0,10.03,0,.3-.02.6.04.86.18.51.26.77.8,1.12,1.22-1.58,1.08-3.18,2.14-4.77,3.22-.08.08-.2.06-.29.02-2.91-1.05-5.81-2.09-8.72-3.14-.07,0-.13-.08-.08-.14.26-.32.51-.66.8-.96ZM16,6.94c1.16-.77,2.31-1.57,3.48-2.33.73.95,1.46,1.9,2.18,2.85.42.54.45,1.35.05,1.9-1.61,2.16-3.23,4.32-4.84,6.49-.09-.05-.18-.1-.26-.16-2.22-1.55-4.46-3.08-6.68-4.64.2-.16.41-.29.62-.43,1.82-1.22,3.63-2.45,5.45-3.67h0ZM2.33,7.49c.77-.95,1.53-1.89,2.3-2.83,2.73.98,5.46,1.97,8.19,2.95.19.07.38.13.56.21-.09.07-.18.14-.27.2-2.64,1.77-5.27,3.56-7.91,5.32-.98-1.3-1.94-2.62-2.91-3.92-.42-.56-.4-1.38.03-1.92h0ZM6.25,13.5c1.01-.67,2.02-1.37,3.04-2.04,2.38,1.65,4.77,3.3,7.14,4.96-1.03,1.39-2.07,2.77-3.1,4.15-.19.28-.44.53-.75.66-.66.3-1.5.08-1.92-.51-1.67-2.27-3.35-4.53-5.01-6.8.19-.16.4-.29.61-.43h0Z"
			fill="currentColor"
			stroke="none"
		/>
	),
	gradleaders: (
		<path
			d="M11.19,3.31c.35-.11.72-.33,1.1-.18,3.72,1.31,7.45,2.62,11.17,3.94.66.15.73,1.17.11,1.42-1.06.4-2.13.74-3.19,1.13-.3.12-.67.16-.92-.08-.39-.31-.34-1.01.11-1.24.44-.21.91-.33,1.36-.49-2.98-1.05-5.95-2.12-8.93-3.14-2.99,1.01-5.96,2.09-8.94,3.14,2.97,1.07,5.95,2.09,8.92,3.16,1.06-.36,2.1-.75,3.15-1.12-.85-.34-1.7-.66-2.55-.99-.33-.15-.72.02-1.04-.16-.53-.25-.71-1.01-.35-1.48.33-.5,1.15-.57,1.53-.09.23.29.6.36.93.5,1.29.5,2.58,1,3.87,1.5.28.08.53.31.56.61.07,2.61.02,5.22.05,7.83.19.39.34.81.15,1.24.28.51.61,1,.86,1.52.13.27-.06.61-.36.59-.91,0-1.83,0-2.75,0-.32.05-.58-.33-.41-.6.25-.51.57-.99.85-1.48-.16-.44-.02-.88.15-1.29.01-2.19-.05-4.39-.02-6.59-1.44.51-2.87,1.03-4.31,1.53-.27.11-.55,0-.81-.09-1.91-.68-3.82-1.35-5.73-2.02,0,1.69,0,3.38,0,5.08,0,.23.19.39.35.53.44.34.98.54,1.5.72,2,.64,4.12.75,6.21.67.58-.03.94.72.61,1.18-.27.42-.82.33-1.25.37-1.94.03-3.92-.13-5.79-.69-.92-.29-1.86-.67-2.56-1.36-.33-.34-.58-.79-.58-1.27,0-1.92,0-3.83,0-5.75-1.21-.44-2.43-.86-3.64-1.29-.77-.14-.79-1.34-.02-1.5,3.54-1.25,7.09-2.5,10.63-3.75h0Z"
			fill="currentColor"
			stroke="none"
		/>
	),
	graduway: (
		<path
			d="M12,3.44L0,9.83l1.78.95v9.78h1.28v-9.1l8.95,4.77,12-6.39L12,3.44ZM12,5.79l7.59,4.04-7.59,4.04V5.79h0Z"
			fill="currentColor"
			stroke="none"
		/>
	),
	handshake: (
		<path
			d="M15.46,2l-1.34,7.62-3.95,3.22,1.91-10.84h-3.82l-3.54,20h3.83l.98-5.53,3.96-3.24-1.56,8.78h3.83l3.54-20h-3.83,0Z"
			fill="currentColor"
			stroke="none"
		/>
	),
	linkedin: (
		<path
			d="M6.48,22H2.33v-13.35h4.15v13.35ZM4.4,6.83c-1.33,0-2.4-1.1-2.4-2.42,0-.64.25-1.25.71-1.7.45-.45,1.06-.71,1.7-.71s1.25.25,1.7.71c.45.45.71,1.06.71,1.7,0,1.33-1.08,2.42-2.4,2.42ZM22,22h-4.14v-6.5c0-1.55-.03-3.54-2.16-3.54s-2.49,1.68-2.49,3.42v6.61h-4.14v-13.35h3.98v1.82h.06c.55-1.05,1.91-2.16,3.92-2.16,4.2,0,4.97,2.76,4.97,6.35v7.33h0Z"
			fill="currentColor"
			stroke="none"
		/>
	),
	peoplegrove: (
		<path
			d="M1.6,13.36c.89,0,1.6-.71,1.6-1.6s-.71-1.6-1.6-1.6-1.6.71-1.6,1.6.71,1.6,1.6,1.6ZM9.81,4.55c0,1.71.71,3.19,2.19,4.55,1.48-1.3,2.19-2.84,2.19-4.55s-.71-3.25-2.19-4.55c-1.48,1.3-2.19,2.84-2.19,4.55ZM9.81,19.09c0,1.71.71,3.19,2.19,4.55,1.48-1.3,2.19-2.84,2.19-4.55s-.71-3.25-2.19-4.55c-1.48,1.3-2.19,2.84-2.19,4.55ZM4.61,10.11c1.48.83,3.13.95,5.02.35-.41-1.95-1.36-3.31-2.84-4.2s-3.19-1-5.02-.35c.41,1.95,1.36,3.31,2.84,4.2ZM17.2,17.38c1.48.83,3.13.95,5.02.35-.41-1.95-1.36-3.31-2.84-4.2-1.48-.83-3.19-1-5.02-.35.41,1.95,1.36,3.31,2.84,4.2ZM6.8,17.38c1.48-.83,2.42-2.25,2.84-4.2-1.89-.59-3.55-.47-5.02.35-1.48.89-2.42,2.25-2.84,4.2,1.89.59,3.55.47,5.02-.35ZM19.39,10.11c1.48-.83,2.42-2.25,2.84-4.2-1.89-.59-3.55-.47-5.02.35-1.48.89-2.42,2.25-2.84,4.2,1.89.59,3.55.47,5.02-.35ZM8.22,21.58c.47-.77.18-1.77-.59-2.19-.77-.47-1.77-.18-2.19.59-.47.77-.18,1.77.59,2.19.77.47,1.77.18,2.19-.59ZM18.03,22.17c.77-.47,1.06-1.42.59-2.19s-1.42-1.06-2.19-.59c-.77.47-1.06,1.42-.59,2.19s1.42,1.06,2.19.59ZM22.4,13.36c.89,0,1.6-.71,1.6-1.6s-.71-1.6-1.6-1.6-1.6.71-1.6,1.6.71,1.6,1.6,1.6ZM18.62,3.55c.47-.77.18-1.77-.59-2.19-.77-.47-1.77-.18-2.19.59-.47.77-.18,1.77.59,2.19.77.47,1.77.18,2.19-.59ZM7.63,4.14c.77-.47,1.06-1.42.59-2.19s-1.42-1.06-2.19-.59-1.06,1.42-.59,2.19,1.42,1.06,2.19.59Z"
			fill="currentColor"
			stroke="none"
		/>
	),
	symplicity: (
		// Symplicity's logo is really a gradient square, but it looks very heavy as an icon.
		// Unlike the other logos, we'll use the stroke for this one to give it a lighter feel.
		<rect x="3" y="3" width="18" height="18" />
	),
	vault: (
		<path
			d="M2,3.5l8.21,17.01h3.46L22,3.5h-3.56l-6.5,13.57L5.56,3.5h-3.56Z"
			fill="currentColor"
			stroke="none"
		/>
	),
}

export type IconName = keyof typeof ICONS
export const ICON_NAMES = Object.keys(ICONS) as IconName[]
